"use client";

import React from "react";
import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Order, OrderStatus } from "@/types/models/order";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { formatCurrency } from "@/utils/currency";
import { Eye } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const getStatusColor = (status: OrderStatus) => {
  switch (status) {
    case "CREATED":
      return "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20";
    case "CONFIRMED":
      return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
    case "SHIPPING":
      return "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20";
    case "RETURNING":
      return "bg-yellow-500/20 text-yellow-600 hover:bg-yellow-500/20";
    case "COMPLETED":
      return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
    case "CANCELLED":
      return "bg-red-500/20 text-red-600 hover:bg-red-500/20";
    default:
      return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";
  }
};

const formatStatus = (status: OrderStatus) => {
  switch (status) {
    case "CREATED":
      return "Created";
    case "CONFIRMED":
      return "Confirmed";
    case "SHIPPING":
      return "Shipping";
    case "RETURNING":
      return "Returning";
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return (status as string).replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
  }
};

interface CustomerOrderActionsCellProps {
  order: Order;
}

const CustomerOrderActionsCell: React.FC<CustomerOrderActionsCellProps> = ({
  order,
}) => {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();

  const handleViewOrder = () => {
    navigate(`/${storeType}/${storeId}/orders/${order.id}`);
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleViewOrder}
        className="h-8 w-8 p-0"
      >
        <Eye className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const customerOrderColumns: ColumnDef<Order>[] = [
  {
    accessorKey: "order_number",
    size: 130,
    minSize: 100,
    maxSize: 160,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Order No.</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const orderNumber = row.getValue("order_number") as number;
      const date = new Date(row.original.created_at);
      const formattedDate = format(date, "MMM dd yyyy");
      return (
        <div>
          <h3 className="font-mono text-xs font-semibold">
            #{orderNumber || "N/A"}
          </h3>
          <span className="text-xs text-muted-foreground">{formattedDate}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Date</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const date = new Date(row.getValue("created_at") as string);
      const formattedDate = format(date, "MMM dd, yyyy");
      return (
        <div>
          <span className="text-sm font-medium">{formattedDate}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "order_status",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const status = row.getValue("order_status") as OrderStatus;
      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${getStatusColor(status)}`}
        >
          {formatStatus(status)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "calculation",
    size: 100,
    minSize: 80,
    maxSize: 120,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Amount</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const calculation = row.getValue("calculation") as Order["calculation"];
      const total = calculation?.grand_total_int || 0;
      return (
        <div>
          <h3 className="text-xs font-semibold">{formatCurrency(total)}</h3>
        </div>
      );
    },
  },
  {
    accessorKey: "payment_status",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Payment</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const paymentStatus = row.getValue("payment_status") as string;
      const paymentType = row.original.supported_payment_type?.[0];

      // Format payment status for display
      let displayStatus = "Unknown";
      switch (paymentStatus) {
        case "NULL":
          displayStatus = "Pending";
          break;
        case "CREATED":
          displayStatus = "Created";
          break;
        case "AUTHORIZED":
          displayStatus = "Authorized";
          break;
        case "CAPTURED":
          displayStatus = "Paid";
          break;
        case "FAILED":
          displayStatus = "Failed";
          break;
        case "REFUNDED":
          displayStatus = "Refunded";
          break;
        case "CANCELLED":
          displayStatus = "Cancelled";
          break;
        default:
          displayStatus = paymentStatus || "Unknown";
      }

      return (
        <div>
          <h3 className="capitalize text-xs font-semibold">{displayStatus}</h3>
          {paymentType && (
            <span className="text-xs text-gray-600 capitalize">
              {paymentType.toLowerCase()}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 100,
    minSize: 80,
    maxSize: 120,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }) => <CustomerOrderActionsCell order={row.original} />,
  },
];
